/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXSelector } from '@cloudio-saas/statex';
import { Box, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { constants, ThemeBGColorProp, ThemeColor } from 'componentregistry';
import { helpTextSelector } from 'designer/state';
import Icon from 'icons/Icon';
import React, { memo } from 'react';
import { useThemeColor } from 'theme/useThemeColor';
import clickOnEnterOrSpace from 'utils/clickOnEnterOrSpace';
import { getDefaultValues } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const { themeColor } = constants;

const useOptionStyles = makeStyles(() => ({
  option: () => ({
    alignSelf: 'center',
    borderRadius: 16,
    cursor: 'pointer',
    flex: '0 0 16px',
    height: 16,
    margin: '0 4px',
    outline: 'none',
    overflow: 'hidden',
    padding: 0,
    position: 'relative',
    width: 16,
  }),
  disabled: {
    pointerEvents: 'none',
    opacity: 0.4,
  },
}));

function Option({
  value,
  label,
  // selected,
  onSelect,
  disabled = false,
}: {
  value: ThemeColor;
  label: string;
  // selected?: boolean;
  onSelect: (v: ThemeColor) => void;
  disabled?: boolean;
}) {
  const classes = useOptionStyles({ color: value });
  const themeClasses = useThemeColor(value);
  const onClick = () => onSelect(value);
  return (
    // eslint-disable-next-line jsx-a11y/control-has-associated-label
    <div
      onKeyDown={(e) => clickOnEnterOrSpace(e, onClick)}
      role="button"
      tabIndex={0}
      data-tip={label}
      onClick={onClick}
      className={clsx(
        classes.option,
        themeClasses.colorAndBackground,
        disabled ? classes.disabled : null,
      )}
    />
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 0 0',
  },
  inner: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    position: 'relative',
    height: '24px',
  },
  label: {
    minWidth: 80,
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
    marginRight: 8,
  },
  s: ({ color }: { color?: ThemeColor }) => ({
    backgroundColor: theme.palette.background.default,
    position: 'absolute',
    top: 0,
    bottom: 0,
    borderRadius: 24,
    border: `2px solid ${
      color === 'inherit' ||
      color === 'default' ||
      color === 'paper' ||
      color === 'ioGrey'
        ? '#aaa'
        : theme.palette[color ?? 'primary']?.main ?? '#aaa'
    }`,
    boxShadow: 'rgb(0 0 0 / 25%) 0px 1px 6px 0px',
    transition: 'all 0.2s cubic-bezier(0.2,0,0,1)',
  }),
  tip: { marginLeft: 8 },
}));

function ThemeBGColorOptions({
  value,
  keys,
  onSelect,
  disabled = false,
}: {
  keys: ThemeColor[];
  value: ThemeColor;
  onSelect: (v: ThemeColor) => void;
  disabled?: boolean;
}) {
  const classes = useStyles({ color: value });

  return (
    <div className={classes.inner}>
      <Box
        sx={{
          width: '24px',
          left: `${24 * keys.indexOf(value)}px`,
        }}
        className={classes.s}
      />
      {keys.map((key) => (
        <Option
          disabled={disabled}
          key={key}
          value={key}
          label={themeColor[key]}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

function ThemeBGColorProperty({
  appUid,
  activeItemId,
  def,
  pageId,
  property,
  mgr,
  value = getDefaultValues<ThemeColor>(mgr, property),
  onChange,
  isViewOnlyUser = false,
}: PropertyEditorProps<ThemeBGColorProp, ThemeColor>) {
  const classes = useStyles({ color: value });
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  let keys = Object.keys(themeColor) as ThemeColor[];
  keys = def.filterColor ? keys.filter(def.filterColor) : keys;

  return (
    <div data-testid={property} className={classes.root}>
      <div className={classes.label}>{def.label}</div>
      <ThemeBGColorOptions
        disabled={isViewOnlyUser}
        value={value}
        keys={keys}
        onSelect={(v) => onChange(property, v)}
      />
      {helpText ? (
        <Icon
          className={classes.tip}
          data-tip-at="top"
          data-tip-delay={0}
          data-tip={helpText}
          iconName="comment-exclamation"
        />
      ) : null}
    </div>
  );
}

export default memo(ThemeBGColorProperty);
